import React from 'react';
import { useRecordContext } from 'react-admin';
import { Chip, Stack } from '@mui/material';
import { isPreplanOnly } from '../funerals/preneedMode';
import { Funeral } from '../types';

interface ContractSummary {
  id: number;
  is_preneed: boolean;
  // `null` for a contract that exists but has never been sent. The view used to
  // label that 'created'; it no longer emits a status it can't observe.
  status: 'paid' | 'signed' | 'sent' | null;
}

type ChipStyle = { color: string; backgroundColor: string; label?: string };

const statusStyles: Record<string, ChipStyle> = {
  paid: { color: '#1b5e20', backgroundColor: '#c8e6c9', label: 'Paid' },
  signed: { color: '#01579b', backgroundColor: '#b3e5fc', label: 'Signed' },
  sent: { color: '#e65100', backgroundColor: '#ffe0b2', label: 'Sent' },
};

// No status yet: the chip is the contract id alone, in neutral grey. It says a
// contract exists without claiming anything has been done with it.
const NO_STATUS_STYLE: ChipStyle = { color: '#424242', backgroundColor: '#ebebeb' };
const PREPLAN_STYLE: ChipStyle = { ...NO_STATUS_STYLE, label: 'Preplan' };

export const ContractStatusChips = () => {
  const record = useRecordContext();
  const contracts: ContractSummary[] = record?.contracts_summary || [];

  if (contracts.length === 0) return null;

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {contracts.map((contract) => {
        // Pre-plan only (T-172): the pre-need contract isn't expected to move.
        const style =
          contract.is_preneed && isPreplanOnly(record as Partial<Funeral>)
            ? PREPLAN_STYLE
            : (contract.status && statusStyles[contract.status]) || NO_STATUS_STYLE;
        const prefix = contract.is_preneed ? `PN ${contract.id}` : `${contract.id}`;
        const label = style.label ? `${prefix} - ${style.label}` : prefix;

        return (
          <Chip
            key={contract.id}
            label={label}
            size="small"
            sx={{
              backgroundColor: style.backgroundColor,
              color: style.color,
              fontWeight: 500,
              border: `1px solid ${style.color}40`,
            }}
          />
        );
      })}
    </Stack>
  );
};
